import React, { useEffect, useState } from "react";
import api, { errMsg } from "../../core/api";
import RecordPage from "../../components/RecordPage";
import Modal from "../../components/Modal";
import { useApp } from "../../core/AppContext";
import { formatDate, todayIso } from "../../core/format";
import { wearPrint } from "../../core/printConfigs";

const STATUS = [
  { value: "pending", label: "در انتظار تأیید" },
  { value: "approved", label: "تأیید شد" },
  { value: "rejected", label: "رد شد" },
];
const LABEL = { pending: "در انتظار تأیید", approved: "تأیید شد", rejected: "رد شد" };
const TONE = { pending: "warn", approved: "ok", rejected: "bad" };

function RejectModal({ onClose, onSubmit }) {
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (text.trim().length < 3) return setErr("دلیل رد را بنویسید");
    setBusy(true);
    setErr("");
    try { await onSubmit(text.trim()); } catch (e) { setErr(errMsg(e)); setBusy(false); }
  };
  return (
    <Modal small title="رد درخواست فرسودگی" onClose={() => !busy && onClose()}
      footer={<>
        <button className="btn btn-danger" onClick={go} disabled={busy}>{busy ? "..." : "رد"}</button>
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>انصراف</button>
      </>}>
      {err && <div className="alert err">{err}</div>}
      <div className="field">
        <label>دلیل رد (برای ثبت‌کننده نمایش داده می‌شود)</label>
        <textarea rows={4} maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
      </div>
    </Modal>
  );
}

export default function EquipmentWear({ onChanged }) {
  const { user, toast, confirm } = useApp();
  const admin = user.role === "admin";
  const staff = user.role === "staff";
  const [equipment, setEquipment] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    api.get("/equipment").then((r) => setEquipment(r.data.items)).catch((e) => toast(errMsg(e), "err"));
  }, [toast, tick]);

  const approve = async (r, load) => {
    if (!(await confirm("تأیید شود؟ تجهیز از فهرست تجهیزات خارج و به انبار فرسوده منتقل می‌شود.", { okText: "تأیید و انتقال", danger: false }))) return;
    try {
      await api.post(`/equipment-wear/${r.id}/decision`, { approve: true });
      toast("تأیید شد و تجهیز به انبار فرسوده منتقل شد");
      setTick((t) => t + 1);
      onChanged?.();
    } catch (e) { toast(errMsg(e), "err"); }
  };
  const reject = async (note) => {
    await api.post(`/equipment-wear/${rejectFor.id}/decision`, { approve: false, note });
    toast("رد شد");
    setRejectFor(null);
    setTick((t) => t + 1);
    onChanged?.();
  };

  return (
    <>
      <RecordPage
      printReport={wearPrint}
        key={tick}
        title="فرسودگی تجهیزات"
        subtitle={
          admin ? "ثبت مستقیم (بلافاصله منتقل می‌شود) یا تأیید و رد درخواست کاربران"
            : staff ? "درخواست شما پس از تأیید مسئول ایمنی اعمال می‌شود"
              : "مشاهده‌ی موارد تأییدشده"
        }
        endpoint="/equipment-wear"
        createLabel="ثبت فرسودگی"
        multipart
        canCreate={admin || staff}
        canEdit={false}
        canDelete={false}
        initial={{ wearDate: todayIso() }}
        filters={[{ name: "status", label: "وضعیت", options: STATUS }]}
        columns={[
          { key: "wearDate", label: "تاریخ فرسودگی", render: (r) => formatDate(r.wearDate) },
          { key: "equipmentCode", label: "کد انبار", render: (r) => <b dir="ltr">{r.equipmentCode || "—"}</b> },
          { key: "equipmentName", label: "تجهیزات", render: (r) => <b>{r.equipmentName}</b> },
          { key: "wornCode", label: "کد انبار فرسوده", render: (r) => <span dir="ltr">{r.wornCode || "—"}</span> },
          { key: "reason", label: "علت", render: (r) => <div className="clip" title={r.reason}>{r.reason}</div> },
          { key: "creatorName", label: "ثبت‌کننده" },
          {
            key: "status", label: "وضعیت",
            render: (r) => (
              <div style={{ display: "grid", gap: 4, justifyItems: "start" }}>
                <span className={`badge ${TONE[r.status]}`}>{LABEL[r.status]}</span>
                {r.decisionNote && <small className="hint">{r.decisionNote}</small>}
              </div>
            ),
          },
        ]}
        getFields={() => [
          {
            name: "equipmentId", label: "تجهیزات", type: "select", required: true, full: true,
            options: equipment.map((e) => ({ value: e.id, label: `${e.code ? `${e.code} — ` : ""}${e.name} — ${e.kind} — ${e.location}` })),
          },
          { name: "wearDate", label: "تاریخ فرسودگی", type: "date", required: true, full: true },
          { name: "reason", label: "علت فرسودگی و خارج کردن از سرویس", type: "textarea", required: true, full: true },
          { name: "file", label: "فایل / عکس (اختیاری)", type: "file", full: true, hint: "PDF یا تصویر تا ۵ مگابایت" },
        ]}
        rowExtra={(r, load) =>
          admin && r.status === "pending" ? (
            <>
              <button className="btn btn-sm" onClick={() => approve(r, load)}><i className="fa-solid fa-check" /> تأیید</button>
              <button className="btn btn-sm btn-danger" onClick={() => setRejectFor(r)}><i className="fa-solid fa-xmark" /> رد</button>
            </>
          ) : null
        }
      />
      {rejectFor && <RejectModal onClose={() => setRejectFor(null)} onSubmit={reject} />}
    </>
  );
}